import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Network, Loader2, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const W = 820;
const H = 520;

/** Tiny deterministic force layout — repulsion + springs + gravity, no extra deps. */
function layout(nodes, edges, steps = 260) {
  const n = nodes.length;
  if (!n) return [];
  const pts = nodes.map((node, i) => ({
    ...node,
    x: W / 2 + Math.cos((2 * Math.PI * i) / n) * (node.kind === 'library' ? 150 : 250),
    y: H / 2 + Math.sin((2 * Math.PI * i) / n) * (node.kind === 'library' ? 150 : 250),
    vx: 0, vy: 0,
  }));
  const index = new Map(pts.map((p, i) => [p.id, i]));
  const links = edges
    .map((e) => [index.get(e.source), index.get(e.target)])
    .filter(([a, b]) => a != null && b != null);

  for (let s = 0; s < steps; s += 1) {
    const cool = 1 - s / steps;
    for (let i = 0; i < n; i += 1) {
      for (let j = i + 1; j < n; j += 1) {
        let dx = pts[i].x - pts[j].x;
        let dy = pts[i].y - pts[j].y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; d2 = 1; }
        const f = 2600 / d2;
        const d = Math.sqrt(d2);
        pts[i].vx += (dx / d) * f; pts[i].vy += (dy / d) * f;
        pts[j].vx -= (dx / d) * f; pts[j].vy -= (dy / d) * f;
      }
    }
    links.forEach(([a, b]) => {
      const dx = pts[b].x - pts[a].x;
      const dy = pts[b].y - pts[a].y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const f = (d - 110) * 0.045;
      pts[a].vx += (dx / d) * f; pts[a].vy += (dy / d) * f;
      pts[b].vx -= (dx / d) * f; pts[b].vy -= (dy / d) * f;
    });
    pts.forEach((p) => {
      p.vx += (W / 2 - p.x) * 0.006;
      p.vy += (H / 2 - p.y) * 0.006;
      p.x += p.vx * 0.12 * cool;
      p.y += p.vy * 0.12 * cool;
      p.vx *= 0.82; p.vy *= 0.82;
      p.x = Math.max(28, Math.min(W - 28, p.x));
      p.y = Math.max(28, Math.min(H - 28, p.y));
    });
  }
  return pts;
}

export default function CitationNetworkPanel({ userId, projectId }) {
  const [graph, setGraph] = useState(null);
  const [job, setJob] = useState(null);
  const [selected, setSelected] = useState(null);
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await thesisApi.getCitationNetwork(userId, projectId);
      if (res?.graph) setGraph(res.graph);
    } catch (e) { /* not built yet */ }
  }, [userId, projectId]);

  useEffect(() => {
    load();
    return () => { if (poll.current) clearInterval(poll.current); };
  }, [load]);

  const build = async () => {
    try {
      const { job_id } = await thesisApi.startCitationNetwork(userId, projectId,
        { min_shared: 2, max_hubs: 25, refresh: true });
      setJob({ status: 'queued', progress: 'Preparing the citation graph…' });
      let ticks = 0;
      poll.current = setInterval(async () => {
        ticks += 1;
        if (ticks > 100) {          // hard cap ~5 min
          clearInterval(poll.current);
          setJob(null);
          toast.error('Map build timed out — try again');
          return;
        }
        try {
          const j = await thesisApi.insightJob(userId, job_id);
          setJob(j);
          if (j.status === 'ready') {
            clearInterval(poll.current);
            setJob(null);
            setGraph(j.result);
            toast.success(`Map built — ${j.result?.stats?.library_papers} papers, `
              + `${j.result?.stats?.hubs} shared references`);
          } else if (j.status === 'failed') {
            clearInterval(poll.current);
            setJob(null);
            toast.error(j.error || 'Map build failed');
          }
        } catch (e) {
          clearInterval(poll.current);
          setJob(null);
          toast.error(e.response?.data?.detail || e.message);
        }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const positioned = useMemo(
    () => (graph ? layout(graph.nodes || [], graph.edges || []) : []), [graph]);
  const posById = useMemo(
    () => new Map(positioned.map((p) => [p.id, p])), [positioned]);
  const maxDegree = useMemo(
    () => Math.max(1, ...positioned.map((p) => p.degree || 0)), [positioned]);

  return (
    <Card data-testid="citation-network-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <Network className="w-5 h-5 text-[#1d4ed8]" /> Citation network map
          {graph && <Badge variant="outline" className="text-[10px]" data-testid="network-stats">
            {graph.stats?.library_papers} papers · {graph.stats?.internal_citations} internal citations
            · {graph.stats?.hubs} shared references
          </Badge>}
        </CardTitle>
        <CardDescription>
          Built from your library's DOIs via OpenAlex. Blue nodes are your own papers; grey nodes are
          works that several of them cite — those are the classics you may still be missing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={build} disabled={!!job} data-testid="network-build-btn">
            {job ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            {job ? 'Building…' : graph ? 'Rebuild map' : 'Build the map'}
          </Button>
          {job && <span className="text-[11px] text-muted-foreground"
            data-testid="network-progress">{job.progress}</span>}
          {graph?.generated_at && !job && (
            <span className="text-[11px] text-muted-foreground">
              built {new Date(graph.generated_at).toLocaleString()}
            </span>
          )}
        </div>

        {graph && (
          <>
            <div className="rounded-lg border border-border bg-muted/20 overflow-x-auto">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[420px]"
                data-testid="network-svg">
                {(graph.edges || []).map((e, i) => {
                  const a = posById.get(e.source);
                  const b = posById.get(e.target);
                  if (!a || !b) return null;
                  return (
                    <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={e.type === 'cites' ? '#1d4ed8' : '#94a3b8'}
                      strokeOpacity={e.type === 'cites' ? 0.45 : 0.2}
                      strokeWidth={e.type === 'cites' ? 1.4 : 0.9} />
                  );
                })}
                {positioned.map((p) => {
                  const r = 5 + 9 * Math.sqrt((p.degree || 0) / maxDegree);
                  const isSel = selected?.id === p.id;
                  return (
                    <g key={p.id} onClick={() => setSelected(p)} className="cursor-pointer"
                      data-testid={`network-node-${p.id}`}>
                      <circle cx={p.x} cy={p.y} r={r}
                        fill={p.kind === 'library' ? '#1d4ed8' : '#94a3b8'}
                        fillOpacity={p.kind === 'library' ? 0.9 : 0.65}
                        stroke={isSel ? '#0f172a' : 'white'} strokeWidth={isSel ? 2.5 : 1} />
                      {(p.kind === 'library' || (p.degree || 0) > maxDegree * 0.5 || isSel) && (
                        <text x={p.x + r + 3} y={p.y + 3} fontSize="9" fill="currentColor"
                          className="fill-foreground pointer-events-none">
                          {(p.local_title || p.title || '').slice(0, 26)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>

            {selected && (
              <div className="p-3 rounded-lg border border-[#1d4ed8]/30 space-y-1"
                data-testid="network-selected">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="text-[9px]">
                    {selected.kind === 'library' ? 'In your library' : 'Cited by your papers'}
                  </Badge>
                  {selected.year && <Badge variant="outline" className="text-[9px]">{selected.year}</Badge>}
                  <Badge variant="outline" className="text-[9px]">
                    {selected.cited_by_count} citations worldwide
                  </Badge>
                  {selected.shared_by && (
                    <Badge variant="outline" className="text-[9px]">
                      cited by {selected.shared_by} of your papers
                    </Badge>
                  )}
                </div>
                <p className="text-[12px] font-medium">{selected.title}</p>
                {selected.authors && (
                  <p className="text-[11px] text-muted-foreground">{selected.authors}</p>
                )}
                {selected.doi && (
                  <a className="text-[11px] text-[#1d4ed8] hover:underline inline-flex items-center gap-1"
                    href={`https://doi.org/${selected.doi}`} target="_blank" rel="noreferrer">
                    doi:{selected.doi} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            )}

            {!!graph.suggested_reads?.length && (
              <div className="space-y-1.5" data-testid="network-suggestions">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Most co-cited works you may be missing
                </div>
                {graph.suggested_reads.map((s) => (
                  <div key={s.id} className="p-2 rounded border border-border flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium truncate">{s.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {s.authors} {s.year ? `· ${s.year}` : ''} · cited by {s.shared_by} of your papers
                      </p>
                    </div>
                    {s.doi && (
                      <a className="text-[11px] text-[#1d4ed8] hover:underline shrink-0"
                        href={`https://doi.org/${s.doi}`} target="_blank" rel="noreferrer">
                        open
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!!graph.stats?.unresolved && (
              <p className="text-[11px] text-muted-foreground">
                {graph.stats.unresolved} entries had no usable DOI and are not on the map.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
